import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Alert } from "@client/controls/Alert/component";
import { FormField } from "@client/controls/Form/Field/component";
import { default as FancyCheckbox } from "@client/controls/Inputs/Checkbox/component";
import PhoneInput from "@client/controls/Inputs/PhoneInput/component";
import { DynamicSelect } from "@client/controls/Inputs/Select/component";
import AddressInput from "@client/controls/Inputs/TextInput/Address/component";
import { unwrapResponse, unwrapResponsePromise } from "@client/lib/api";
import { getGoogleFontLinkFromNames, isGoogleFontLabel } from "@client/lib/fonts";
import appInfo from "@common/appInfo";
import StripeCardInputFormField from "@common/components/Form/Field/StripeCard/component";
import { FormStyleContext } from "@common/contexts/FormStyle";
import { fieldValidation } from "@common/controls/Form/Field/lib";
import { FormButtons, FormControlled, type FormControlledRenderParams, FormGroup } from "@common/controls/Form/component";
import { getDialCode } from "@common/controls/Inputs/PhoneInput/lib";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import * as React from "react";
import api from "../api";
import { getBillingFrequency, getBottomInfo, getCodes, getInitialPrice, getTitle, getTopInfo } from "../lib";
import { type Purchase } from "../types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  error: string | null;
  fieldStyle: React.CSSProperties;
  firstStepData: Purchase.View.FirstStepFormData;
  isMobile: boolean;
  isLabelInside: boolean;
  package: Purchase.Package;
  payment: Purchase.Api.Payment;
  trial: Purchase.Api.Trial | null;
  verifyResult: Purchase.Api.VerifyUserResponse;
};
export interface ActionsProps {
  next: (res: Purchase.Api.AssignSystemResponse) => void;
  failed: (error: Purchase.Api.ErrorCode) => void;
  dismissError: () => void;
}
export type Props = MapProps & ActionsProps;

export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const defaultValues = React.useMemo(
    (): Partial<Purchase.View.SecondStepFormData> => ({
      countryCode: appInfo.countryCode,
      telephone: getDialCode(appInfo.countryCode) ?? ""
    }),
    []
  );

  const [countryCode, setCountryCode] = React.useState(defaultValues.countryCode ?? null);

  const submit = React.useCallback(
    async (data: Purchase.View.SecondStepFormData) => {
      const resp = await api.assignSystem(props.apiInfo, {
        ...data,
        ...props.verifyResult.user,
        ...getCodes(),
        billingFrequencyKey: getBillingFrequency(props.package),
        localTime: new Date().toTimeString(),
        timeZoneIana: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      const error = resp.error;
      if (error === "DEPLOYING_WEBSITE") {
        props.failed(error);
      } else {
        if (error) throw new Error(error);

        props.next(unwrapResponse(resp));
      }
    },
    [props.apiInfo, props.package, props.verifyResult.user, props.next, props.package.id]
  );

  const { fieldStyle } = React.useContext(FormStyleContext);

  return (
    <>
      {!props.verifyResult.isAvailable && <Alert color={SystemColor.Red}>{i18n.infoUnavailable}</Alert>}

      <div className="second_step">
        <h3>{getTitle(i18n, props.package, props.trial, countryCode)}</h3>

        <FormControlled defaultValues={defaultValues} submit={submit}>
          {({
            isSubmitting,
            onChange,
            submit,
            values
          }: FormControlledRenderParams<Purchase.View.SecondStepFormData>) => (
            <>
              {getTopInfo(i18n, props.package)}

              <FormGroup>
                <FormField
                  colSpan={6}
                  Component={DynamicSelect}
                  componentProps={{ options: () => options.countries(), sort: "asc" }}
                  isRequired
                  label={i18n.country}
                  name="countryCode"
                  value={values.countryCode}
                  onChange={(n, v) => {
                    onChange("countryCode", v);
                    onChange("subdivisionCode", null);

                    setCountryCode(v);
                  }}
                />

                <FormField
                  colSpan={6}
                  Component={DynamicSelect}
                  componentProps={{
                    dependencies: { countryCode: values.countryCode },
                    options: () => options.regions(values.countryCode),
                    sort: "asc"
                  }}
                  isRequired
                  label={i18n.region}
                  name="subdivisionCode"
                  requiredBehaviour="optionalIfEmpty"
                  value={values.subdivisionCode}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  colSpan={6}
                  Component={AddressInput}
                  isRequired
                  label={i18n.city}
                  name="city"
                  value={values.city}
                />
                <FormField
                  colSpan={6}
                  Component={AddressInput}
                  isRequired
                  label={i18n.postalCode}
                  name="postalCode"
                  value={values.postalCode}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  colSpan={6}
                  Component={AddressInput}
                  isRequired
                  label={i18n.street}
                  name="street"
                  value={values.street}
                />
                <FormField
                  colSpan={6}
                  Component={AddressInput}
                  label={i18n.unitAptSuite}
                  name="unit"
                  value={values.unit}
                />
              </FormGroup>

              <FormField
                colSpan={12}
                Component={PhoneInput}
                isRequired
                label={i18n.phone}
                name="telephone"
                validator={fieldValidation.telephone}
                value={values.telephone}
              />

              <FormGroup>
                <FormField
                  colSpan={values.company ? 6 : 12}
                  Component={AddressInput}
                  label={i18n.company}
                  name="company"
                  value={values.company}
                />
                {!!values.company && (
                  <FormField
                    colSpan={6}
                    Component={AddressInput}
                    label={i18n.taxNumber}
                    name="companyTaxNumber"
                    value={values.companyTaxNumber}
                  />
                )}
              </FormGroup>

              <StripeCardInputFormField
                amount={getInitialPrice(props.package, props.trial)}
                createIntent={async () => {
                  const res = await unwrapResponsePromise(
                    api.payment(props.apiInfo, {
                      ...props.verifyResult.user,
                      ...getCodes(),
                      billingFrequencyKey: getBillingFrequency(props.package),
                      countryCode: values.countryCode,
                      subdivisionCode: values.subdivisionCode
                    })
                  );
                  return {
                    isSetup: !getInitialPrice(props.package, props.trial),
                    secret: res.clientSecret
                  };
                }}
                currencyCode="USD"
                customer={props.verifyResult.user}
                fontCssSrc={
                  props.fieldStyle.fontFamily && isGoogleFontLabel(props.fieldStyle.fontFamily)
                    ? getGoogleFontLinkFromNames([props.fieldStyle.fontFamily])
                    : undefined
                }
                inputStyle={fieldStyle}
                isPaddingOnContainer
                isRequired
                label={props.isLabelInside ? "" : i18n.card}
                name="stripeIntentId"
                publishableKey={props.payment.publishableKey}
                value={values.stripeIntentId}
              />

              {props.isMobile && (
                <img
                  className="stripe"
                  src="https://d1qgwakyzw6n5u.cloudfront.net/common/images/logos/stripe-black.svg"
                />
              )}

              <FormField
                addon={
                  !props.isMobile && (
                    <img
                      className="stripe"
                      src="https://d1qgwakyzw6n5u.cloudfront.net/common/images/logos/stripe-black.svg"
                    />
                  )
                }
                Component={FancyCheckbox}
                componentProps={{
                  label: (
                    <a style={{ color: "inherit" }} href="https://funnelr.com/legal/terms" target="_blank">
                      {i18n.infoSubscriptionConsent}
                    </a>
                  )
                }}
                isRequired
                name="hasAcceptedTerms"
                value={values.hasAcceptedTerms}
              />

              <FormButtons align="justify">
                <ModuleButton
                  {...getRootContainerProps(props)}
                  isDisabled={isSubmitting || !props.verifyResult.isAvailable}
                  caption={i18n.next}
                  onClick={submit}
                />
              </FormButtons>

              {getBottomInfo(i18n, props.package, props.trial, values.countryCode)}
            </>
          )}
        </FormControlled>
      </div>
    </>
  );
};
